import { useEffect, useRef } from 'react';

// Open dialogs, topmost last: only the top one answers Escape and traps Tab.
const stack = [];
const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal behaviour shared by Modal and the photo lightbox: Escape closes, Tab stays inside
 * the panel, focus moves in on open and goes back to the trigger on close.
 */
export const useDialog = ({ isOpen, onClose, panelRef, initialFocusRef = null, dismissible = true }) => {
  const onCloseRef = useRef(onClose);
  const dismissibleRef = useRef(dismissible);
  onCloseRef.current = onClose;
  dismissibleRef.current = dismissible;

  useEffect(() => {
    if (!isOpen) return undefined;
    const token = {};
    stack.push(token);
    const previouslyFocused = document.activeElement;

    const focusTimer = setTimeout(() => {
      const target = initialFocusRef?.current || panelRef.current;
      target?.focus?.({ preventScroll: true });
    }, 0);

    const onKeyDown = (e) => {
      if (stack[stack.length - 1] !== token || e.defaultPrevented) return;
      if (e.key === 'Escape') {
        if (dismissibleRef.current) onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const nodes = [...panelRef.current.querySelectorAll(FOCUSABLE)].filter((n) => n.getClientRects().length > 0);
      if (nodes.length === 0) {
        e.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      // Focus may have fallen to <body> (e.g. the focused button unmounted): bring it back in.
      if (!panelRef.current.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && (active === first || active === panelRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      stack.splice(stack.indexOf(token), 1);
      if (previouslyFocused?.focus && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useDialog;
